import { SetStateAction, useContext } from "react";

import { useTranslation } from "react-i18next";

import { AxiosError } from "axios";
import { TFunction } from "i18next";

import { RESPONSE_MESSAGES } from "../../api/types";
import { AlertContext } from "../../context/alert/AlertContextProvider";
import { ALERT_STATUS } from "../../context/alert/types";
import { VehicleErrorMessages } from "../types";

const useResponse = () => {
  const { setAlert } = useContext(AlertContext);
  const { i18n } = useTranslation();

  const handleResponse = (
    status: ALERT_STATUS,
    message: string,
    description?: string
  ) => {
    if (message === RESPONSE_MESSAGES.CanNotReadToken) {
      return;
    }
    // don't display alert snackbar on error 500
    if (message === RESPONSE_MESSAGES.Status500) {
      return;
    }

    const safeMessage =
      typeof message === "string" ? message : JSON.stringify(message);

    return setAlert(status, safeMessage, description);
  };

  const handleCommonError = (
    error: any,
    t: TFunction<"translation", undefined>
  ) => {
    const err = error as AxiosError<any>;
    if (err) {
      const responseData: string = err.response?.data
        ? t(`Alert##${err.response?.data?.message}`)
        : t("Alert##action failed");

      const isMessageInTranslations = i18n.exists(
        `Alert##${err.response?.data?.message}`
      );

      isMessageInTranslations
        ? handleResponse(ALERT_STATUS.Critical, responseData)
        : handleResponse(
            ALERT_STATUS.Critical,
            //  if defined - display message from response, otherwise display default message
            err.response?.data?.message || t("Alert##action failed")
          );
    }
  };

  const handleErrorWithEntries = (
    error: any,
    t: TFunction<"translation", undefined>
  ) => {
    const err = error as AxiosError<any>;
    if (err) {
      const responseMessage = err.response?.data.message;

      const translatedMessage =
        responseMessage && t(`Alert##${responseMessage}`);

      const responseEntries = err.response?.data?.entries;
      const responseEntriesMessage =
        err.response?.data !== "" &&
        responseEntries.length > 0 &&
        t(`Alert##${err.response?.data?.entries[0]?.message}`);
      const errorMessage =
        responseEntriesMessage || translatedMessage || err.message;
      handleResponse(ALERT_STATUS.Critical, errorMessage);
    }
  };

  const handleVehicleActionError = (
    error: any,
    setIsLoading: (value: SetStateAction<boolean>) => void,
    setImeiError: (value: SetStateAction<string | null>) => void,
    setNameInputError: (value: SetStateAction<string | null>) => void,
    t: TFunction<"translation", undefined>,
    additionalErrorAction?: () => void
  ) => {
    const err = error as AxiosError<any>;
    const errorMessage: string = err.response?.data?.message;

    switch (errorMessage) {
      case VehicleErrorMessages.ImeiAlreadyAssignedAccessible:
      case VehicleErrorMessages.ImeiAlreadyAssignedInaccessible:
      case VehicleErrorMessages.ImeiChangeError:
        return additionalErrorAction?.();

      case VehicleErrorMessages.ImeiExists:
        setIsLoading(false);
        return setImeiError(t(`Alert##${errorMessage}`));

      case VehicleErrorMessages.VrnExists:
        setIsLoading(false);
        return setNameInputError(t(`Alert##${errorMessage}`));

      default:
        return handleCommonError(error, t);
    }
  };

  return {
    handleCommonError,
    handleErrorWithEntries,
    handleResponse,
    handleVehicleActionError
  };
};

export default useResponse;
